import { Box3, Mesh, type Scene, Vector3 } from 'three';
import type { ZoneDef } from '../../game/zones';
import { loadMaterials } from '../materials';
import type { BuiltLevel } from '../TestLevel';
import { buildCollisionMesh } from './collision';
import { buildDisplacementFaces } from './displacement';
import { type Entity, parseVec3, readEntities } from './entities';
import { buildVisualMesh, triangleCount } from './faceGeometry';
import { facesToGeometry } from './faces';
import { type Model, readModels } from './lumps';
import { BspReader } from './reader';
import { srcArrToWorld, srcYawToWorld } from './transform';

/**
 * Turns an uploaded Source `.bsp` (CS surf map) into a playable `BuiltLevel`:
 * collision from the brush lumps, visuals from the face lumps, and spawn +
 * teleport zones from the entity lump. Mirrors `glbLevel.ts`'s loader shape so it
 * drops straight into `switchMap` / `LevelCollider`. We render the visual faces
 * and collide against the (unrendered) brush mesh, so what you ride matches Source
 * exactly while `playerclip` stays invisible.
 *
 * Timed runs are NOT auto-detected: surf timer triggers are defined by server
 * plugins, not the map, so there's no reliable in-BSP signal — imported maps load
 * as free-surf (no start/end zones). Teleports (resets/skips) and spawn DO map.
 */

interface MappedEntities {
  spawn: Vector3 | null;
  spawnYaw: number;
  zones: ZoneDef[];
}

const SPAWN_CLASSES = [
  'info_player_terrorist',
  'info_player_counterterrorist',
  'info_player_start',
];

/** World-space AABB of a brush-entity model ("*N" → MODELS[N]). */
function modelBounds(
  ref: string | undefined,
  models: Model[],
): { center: Vector3; size: Vector3 } | null {
  if (ref?.[0] !== '*') return null;
  const m = models[Number(ref.slice(1))];
  if (!m) return null;
  const a = srcArrToWorld(m.min);
  const b = srcArrToWorld(m.max);
  const min = new Vector3(Math.min(a.x, b.x), Math.min(a.y, b.y), Math.min(a.z, b.z));
  const max = new Vector3(Math.max(a.x, b.x), Math.max(a.y, b.y), Math.max(a.z, b.z));
  return { center: min.clone().add(max).multiplyScalar(0.5), size: max.clone().sub(min) };
}

/** Map BSP entities to a spawn point + teleport zones (pure, unit-tested). */
export function mapEntities(entities: Entity[], models: Model[]): MappedEntities {
  const byTargetname = new Map<string, Entity>();
  for (const e of entities) {
    if (e.targetname) byTargetname.set(e.targetname, e);
  }

  let spawn: Vector3 | null = null;
  let spawnYaw = 0;
  const spawnEnt =
    entities.find((e) => e.classname != null && SPAWN_CLASSES.includes(e.classname)) ??
    entities.find((e) => e.classname === 'info_teleport_destination');
  if (spawnEnt) {
    const o = parseVec3(spawnEnt.origin);
    if (o) {
      spawn = srcArrToWorld(o);
      const ang = parseVec3(spawnEnt.angles);
      spawnYaw = srcYawToWorld(ang ? ang[1] : 0);
    }
  }

  const zones: ZoneDef[] = [];
  for (const e of entities) {
    if (e.classname !== 'trigger_teleport') continue;
    const bounds = modelBounds(e.model, models);
    const destEnt = e.target ? byTargetname.get(e.target) : undefined;
    const dest = destEnt ? parseVec3(destEnt.origin) : null;
    if (bounds && dest) {
      zones.push({
        kind: 'teleport',
        center: bounds.center,
        size: bounds.size,
        dest: srcArrToWorld(dest),
      });
    }
  }

  return { spawn, spawnYaw, zones };
}

/** Decompress a gzip ArrayBuffer using the native browser API (no dependency). */
async function gunzip(buf: ArrayBuffer): Promise<ArrayBuffer> {
  const stream = new Blob([buf]).stream().pipeThrough(new DecompressionStream('gzip'));
  return await new Response(stream).arrayBuffer();
}

/**
 * Load a .bsp (or gzipped .bsp.gz) map from a URL — the built-in CS-map path.
 * Shipped maps are stripped + gzipped (`scripts/fetch-maps.mts`) to a couple MB;
 * we gunzip in the browser and hand the raw BSP to `loadBspLevel`.
 */
export async function loadBspFromUrl(
  mapId: string,
  url: string,
  scene: Scene,
): Promise<BuiltLevel> {
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`Failed to fetch ${url}: ${res.status}`);
  }
  let buf = await res.arrayBuffer();
  const head = new Uint8Array(buf, 0, Math.min(2, buf.byteLength));
  if (url.endsWith('.gz') || (head[0] === 0x1f && head[1] === 0x8b)) {
    buf = await gunzip(buf);
  }
  return loadBspLevel(mapId, buf, scene);
}

/** Load an uploaded .bsp into a BuiltLevel (browser runtime path). */
export async function loadBspLevel(
  mapId: string,
  buffer: ArrayBuffer,
  scene: Scene,
): Promise<BuiltLevel> {
  const reader = new BspReader(buffer);
  if (reader.hasCompressedLumps()) {
    throw new Error(
      'This map has LZMA-compressed BSP lumps (compiled with lump compression), which the importer does not support yet. Use an uncompressed build of the map.',
    );
  }
  const materials = await loadMaterials();

  const collision = buildCollisionMesh(reader, materials.ramp); // brushes + displacements
  const visual = buildVisualMesh(reader, materials.ramp); // brush faces (skips disp base quads)
  const dispFaces = buildDisplacementFaces(reader);
  // Render the visual faces + the actual displaced terrain; collide against the
  // (unrendered) brush+disp mesh so playerclip stays invisible and doesn't
  // z-fight. Fall back to rendering the collision mesh if nothing else is visible.
  let shown = false;
  if (triangleCount(visual) > 0) {
    scene.add(visual);
    shown = true;
  }
  if (dispFaces.length > 0) {
    const dispVisual = new Mesh(facesToGeometry(dispFaces), materials.ramp);
    dispVisual.castShadow = false;
    dispVisual.receiveShadow = true;
    scene.add(dispVisual);
    shown = true;
  }
  if (!shown) {
    scene.add(collision);
  }

  collision.geometry.computeBoundingBox();
  const bb =
    collision.geometry.boundingBox ?? new Box3(new Vector3(-1, -1, -1), new Vector3(1, 1, 1));
  const mapped = mapEntities(readEntities(reader), readModels(reader));
  const spawn =
    mapped.spawn ??
    new Vector3((bb.min.x + bb.max.x) / 2, bb.max.y + 64, (bb.min.z + bb.max.z) / 2);

  return {
    mapId,
    colliders: [collision],
    spawn,
    spawnYaw: mapped.spawnYaw,
    zones: mapped.zones,
    killY: bb.min.y - 256,
  };
}
